import React, { useEffect, useState } from 'react';
import { ENUM_SEARCH_TYPE, ENUM_TASK_STATUS, taskStatusList } from './utils';
import { useSearchParams } from 'react-router-dom';
import './styles.css';
import { ENUM_DATE_RANGE, getDateRangeSelector, Paginate } from '@cokitchen/cokitchen-components';
import { apiService } from 'utils/api';
import { IssueType } from 'models/issue.model';
import RepIssueCard from 'components/cs/reps-details/rep-issue-card';
import ReviewModal from 'components/cs/pending-tasks/review-modal/review-modal';
import EscalateIssueModal from 'pages/cs/pending-tasks/escalate-issue-modal';
import { selectedRep } from 'pages/cs/pending-tasks';
import MarkAsDoneModal from 'components/cs/pending-tasks/mark-as-done-modal';
import TaskHistoryFilter from './partials/task-history-filter';
import { TaskHistoryFilterResult } from 'hooks/useTaskHistoryPage';

const TaskHistory = () => {
  const [taskStatus, setTaskStatus] = useSearchParams();
  const taskStatusValue = taskStatus.get('taskStatus') ?? ENUM_TASK_STATUS.ALL.toLowerCase();
  const [filters, setFilters] = useState({
    date: getDateRangeSelector(ENUM_DATE_RANGE.THIS_WEEK)
  });

  const [searchValue, setSearchValue] = useState('');
  const [selectedSearchReturnVal, setSelectedSearchReturnVal] = useState<
    TaskHistoryFilterResult & { type?: ENUM_SEARCH_TYPE }
  >();

  const [selectedTask, setSelectedTask] = useState({} as IssueType);
  const [resolution, setResolution] = useState<string>('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [showMarkModal, setShowMarkModal] = useState(false);

  const [selectedCsRep, setSelectedCsRep] = useState({} as selectedRep);

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [allTask, setAllTask] = useState<IssueType[]>([]);

  const openReviewModal = (issue: IssueType) => {
    setShowReviewModal(true);
    setSelectedTask(issue);
  };
  const openMarkModal = (issue: IssueType) => {
    setSelectedTask(issue);
  };
  const toggleEscalateModal = () => {
    setShowEscalateModal(!showEscalateModal);
  };

  const handleGetAllTask = async () => {
    setLoading(true);
    const queryParams = {
      page: page,
      start_date: filters?.date.startDate,
      end_date: filters?.date.endDate,
      status: taskStatus.get('taskStatus') ?? 'all',
      full_name:
        selectedSearchReturnVal?.type === ENUM_SEARCH_TYPE.REP_NAME &&
        !selectedSearchReturnVal?.isRep
          ? selectedSearchReturnVal?.name
          : '',
      order_code:
        selectedSearchReturnVal?.type === ENUM_SEARCH_TYPE.ORDER_CODE
          ? selectedSearchReturnVal?.name
          : '',
      rep_id:
        selectedSearchReturnVal?.type === ENUM_SEARCH_TYPE.REP_NAME &&
        selectedSearchReturnVal?.isRep
          ? selectedSearchReturnVal?.id
          : ''
    };

    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ([_, value]) => value !== undefined && value !== null && value !== ''
      )
    );
    try {
      const res = await apiService.get('cs/tasks', {
        params: filteredParams,
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      if (res.data.data) {
        setAllTask(res.data.data);
        const totalNoOfPages = Number(res.data.pagination.total);
        setPageCount(totalNoOfPages);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetAllTask();
  }, [page, searchValue, taskStatusValue, filters, selectedSearchReturnVal]);

  return (
    <div>
      <h1 className='text-2xl font-extrabold'>Task History</h1>
      <p className='text-gray-6/60 font-medium mt-1 text-sm'>Manage all tasks</p>
      <div className='flex justify-between items-center gap-6 mt-7 border-b border-gray-10/40 pb-6'>
        <ul className='flex gap-6 items-end h-full'>
          {taskStatusList.map((status) => (
            <li
              onClick={() => setTaskStatus({ taskStatus: status.value.toLowerCase() })}
              key={status.value.toLowerCase()}
              className={`underline_animation_normal ${
                taskStatusValue === status.value.toLowerCase()
                  ? 'underline_animation_selected'
                  : 'underline_animation_notSelected'
              }`}>
              <button className='capitalize'>{status.name}</button>
            </li>
          ))}
        </ul>
        <TaskHistoryFilter
          setSelectedSearchReturnVal={(data) => setSelectedSearchReturnVal(data)}
          setSearchVal={setSearchValue}
          searchVal={searchValue}
          filters={filters}
          setFilters={setFilters}
        />
      </div>
      <section>
        <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6'>
          {allTask.map((t) => (
            <RepIssueCard
              handleMenuClick={() => setSelectedTask(t)}
              issue={t}
              key={t.id}
              isComplete={taskStatusValue === ENUM_TASK_STATUS.RESOLVED ? true : false}
              openReview={() => openReviewModal(t)}
              setShowEscalateModal={setShowEscalateModal}
              markIssue={() => openMarkModal(t)}
            />
          ))}
        </div>

        {loading ? (
          <div className='text-center mt-5 animate-bounce font-semibold'>Loading ...</div>
        ) : allTask.length === 0 ? (
          <div className='text-center mt-10 text-lg'>No task history to show</div>
        ) : null}
        {<Paginate pageCount={pageCount} onPageChange={(data) => setPage(data.selected)} />}
      </section>

      <MarkAsDoneModal
        show={showMarkModal}
        onClose={() => {
          setResolution('');
          setSelectedCsRep({} as selectedRep);
          setShowMarkModal(false);
        }}
        onSuccess={() => {
          handleGetAllTask();
        }}
        issue={selectedTask}
        selectedCsRep={selectedCsRep}
        resolution={resolution}
      />
      <ReviewModal
        show={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
        }}
        type={taskStatusValue === ENUM_TASK_STATUS.RESOLVED ? 'resolved' : 'unresolved'}
        issue={selectedTask}
        resolution={resolution}
        setResolution={setResolution}
        selectedCsRep={selectedCsRep}
        setSelectedCsRep={setSelectedCsRep}
        setShowMarkModal={setShowMarkModal}
      />
      <EscalateIssueModal
        show={showEscalateModal}
        onClose={toggleEscalateModal}
        issue={selectedTask}
      />
    </div>
  );
};

export default TaskHistory;
